import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { getInvoiceStatusPillStyle } from "./invoiceStatusStyle";

const SERIES_DEPOSIT = "Deposit Held";
const SERIES_EXPOSURE = "Exposure";

export const SECURITY_DEPOSIT_BREAKDOWN_COLUMNS = [
  { key: "reference_number", label: "Reference No." },
  { key: "type", label: "Type" },
  { key: "currency", label: "Currency" },
  { key: "transaction_date", label: "Transaction Date", type: "date" },
  { key: "validity_start_date", label: "Validity Start", type: "date" },
  { key: "validity_end_date", label: "Validity End", type: "date" },
  { key: "status", label: "Status", type: "pill" },
  { key: "held", label: "Held", mono: true },
];

export const SECURITY_DEPOSIT_EXPOSURE_COLUMNS = [
  { key: "invoice_number", label: "Invoice No." },
  { key: "status", label: "Status", type: "pill" },
  { key: "currency", label: "Currency" },
  { key: "date", label: "Date", type: "date" },
  { key: "invoice_total", label: "Invoice Total", mono: true },
  { key: "outstanding", label: "Outstanding", mono: true },
];

function formatAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function buildDepositRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: item?.transaction_slug || index,
    reference_number: item?.reference_number || "-",
    type: item?.type || "-",
    currency: item?.currency || "-",
    transaction_date: item?.transaction_date,
    validity_start_date: item?.validity_start_date,
    validity_end_date: item?.validity_end_date,
    status: { label: item?.status || "-", tone: item?.status === "Active" ? "green" : "navy" },
    held: formatAmount(item?.held, item?.currency),
    _lessee: item?.lessee_name || "Unknown",
  }));
}

function buildExposureRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: item?.invoice_slug || item?.invoice_number || index,
    invoice_number: item?.invoice_number || "-",
    status: { label: item?.status || "-", style: getInvoiceStatusPillStyle(item?.status) },
    currency: item?.currency || "-",
    date: item?.date,
    invoice_total: formatAmount(item?.invoice_total, item?.currency),
    outstanding: formatAmount(item?.outstanding, item?.currency),
    _lessee: item?.lessee_name || "Unknown",
  }));
}

// Drives the "Security Deposit Coverage" popup: clicking a lessee's "Deposit Held" bar
// lists its deposit_breakdown entries, clicking its "Exposure" bar lists its exposure_breakdown invoices.
export function useSecurityDepositCoverageBreakdown({ depositBreakdown, exposureBreakdown, onTrack }) {
  const [selectedLessee, setSelectedLessee] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(SERIES_DEPOSIT);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedLessee(null);
    setSelectedSeries(SERIES_DEPOSIT);
    setModalOpen(false);
  }, []);

  const depositRows = useMemo(() => buildDepositRows(depositBreakdown), [depositBreakdown]);
  const exposureRows = useMemo(() => buildExposureRows(exposureBreakdown), [exposureBreakdown]);
  const canRender = depositRows.length > 0 || exposureRows.length > 0;

  const isExposure = selectedSeries === SERIES_EXPOSURE;

  const rows = useMemo(() => {
    const source = isExposure ? exposureRows : depositRows;
    if (!selectedLessee) return source;
    return source.filter((row) => row._lessee === selectedLessee);
  }, [isExposure, exposureRows, depositRows, selectedLessee]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series") return;
      const lessee = params.name || null;
      const series = params.seriesName === SERIES_EXPOSURE ? SERIES_EXPOSURE : SERIES_DEPOSIT;
      setSelectedLessee(lessee);
      setSelectedSeries(series);
      setModalOpen(true);
      onTrack?.(`Graph Bar Clicked: ${lessee || ""} - ${series}`);
    },
    [onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = useMemo(
    () => [selectedLessee, selectedSeries].filter(Boolean).join(" | "),
    [selectedLessee, selectedSeries],
  );

  return { rows, isExposure, canRender, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function SecurityDepositCoverageModal({ open, onClose, rows, isExposure, titleSuffix }) {
  const title = "Security Deposit Coverage";
  const count = Array.isArray(rows) ? rows.length : 0;
  const noun = isExposure ? (count === 1 ? "Invoice" : "Invoices") : (count === 1 ? "Deposit" : "Deposits");
  const countLabel = `${count} ${noun}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix.replace(/ \| /g, " - ")}` : title}
      columns={isExposure ? SECURITY_DEPOSIT_EXPOSURE_COLUMNS : SECURITY_DEPOSIT_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
